import { ApiClient } from '../../ApiClient';
import { ProductDTO } from '../dto/ProductDTO';
import { AddProductPartDTO } from '../dto/AddProductPartDTO';
import { ProductFilterDTO } from '../dto/ProductFilterDTO';
import { CreateProductDTO } from '../dto/CreateProductDTO';
import { Product, ProductUpdate } from '@domain/product/models/Product';
import { ShipmentMethodDTO } from '@infrastructure/purchasing/dto/ShipmentDTO';
import { PaginatedDTO } from '@infrastructure/paginated.dto';;
import { MarketplaceDTO } from '../dto/MarketplaceDTO';
import { ListingCountry } from '@domain/product/models/ListingCountry';

export class ProductDataSource {

  constructor(private api: ApiClient) { }

  async getProducts(filter?: ProductFilterDTO): Promise<PaginatedDTO<ProductDTO[]>> {
    const response = await this.api.get<PaginatedDTO<ProductDTO[]>>('/products', { params: filter });
    return response.data;
  }

  async getProduct(productId: string): Promise<ProductDTO> {
    const response = await this.api.get<ProductDTO>(`/products/${productId}`);
    return response.data;
  }

  async updateProduct(productId: string, updateData: ProductUpdate): Promise<ProductDTO> {
    const response = await this.api.put<ProductDTO>(`/products/${productId}`, updateData);
    return response.data;
  }

  async updateProducts(products: Partial<Product>[]): Promise<ProductDTO[]> {
    const response = await this.api.put<ProductDTO[]>(`/products`, products);
    return response.data;
  }

  async bulkUpdateProducts(body: { updateData: Partial<Product>; filters?: ProductFilterDTO }): Promise<void> {
    const response = await this.api.put<void>('/products', body);
    return response.data;
  }

  async updatedefaultShipmentMethod(
    productId: string,
    shipmentMethodId: string
  ): Promise<ShipmentMethodDTO> {
    const response = await this.api.put<ShipmentMethodDTO>(
      `/products/${productId}/default-shipment-method/${shipmentMethodId}`,
      {}
    );
    return response.data;
  }

  async deleteProducts(filters: ProductFilterDTO): Promise<void> {
    await this.api.delete('/products', { data: { filters } });
  }

  async getProductMarketplaces(): Promise<MarketplaceDTO[]> {
    const response = await this.api.get<MarketplaceDTO[]>('/products/marketplaces');
    return response.data;
  }

  async getListingCountries(): Promise<ListingCountry[]> {
    const response = await this.api.get<ListingCountry[]>('/products/countries');
    return response.data;
  }

  async createProduct(data: CreateProductDTO): Promise<ProductDTO> {
    const response = await this.api.post<ProductDTO>('/products', data);
    return response.data;
  }

  async addProductParts(productId: string, data: AddProductPartDTO[]): Promise<ProductDTO> {
    const response = await this.api.post<ProductDTO>(
      `/products/${productId}/components/parts`,
      data
    );
    return response.data;
  }

  async removeProductPart(productId: string, componentId: string): Promise<void> {
    await this.api.delete(`/products/${productId}/components/parts/${componentId}`);
  }

  async addProductUsedIn(productId: string, data: AddProductPartDTO[]): Promise<ProductDTO> {
    const response = await this.api.post<ProductDTO>(
      `/products/${productId}/components/used-in`,
      data
    );
    return response.data;
  }

  async removeProductUsedIn(productId: string, componentId: string): Promise<void> {
    await this.api.delete(`/products/${productId}/components/used-in/${componentId}`);
  }
}